import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Target,
  Apple,
  Dumbbell,
  Moon,
  Heart,
  Pill,
  Calendar,
  CheckCircle2,
} from "lucide-react";

const categoryIcons = {
  nutrition: Apple,
  exercise: Dumbbell,
  sleep: Moon,
  stress: Heart,
  supplementation: Pill,
  lifestyle: Target,
  testing: Calendar,
};

const priorityColors = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-orange-100 text-orange-700 border-orange-200",
  low: "bg-blue-100 text-blue-700 border-blue-200",
};

export default function PersonalizedRecommendations({ recommendations }) {
  const [completedItems, setCompletedItems] = React.useState(new Set());

  const toggleComplete = (index) => {
    const newCompleted = new Set(completedItems);
    if (completedItems.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
    }
    setCompletedItems(newCompleted);
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-600" />
          Personalized Recommendations
        </CardTitle>
        <p className="text-sm text-slate-600 mt-1">
          AI-powered recommendations based on your latest biomarkers and health
          data
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {recommendations.map((rec, index) => {
            const IconComponent =
              categoryIcons[rec.category.toLowerCase()] || Target;
            const isCompleted = completedItems.has(index);

            return (
              <div
                key={index}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  isCompleted
                    ? "bg-green-50 border-green-200"
                    : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`p-2 rounded-lg ${isCompleted ? "bg-green-100" : "bg-indigo-100"}`}
                  >
                    <IconComponent
                      className={`w-5 h-5 ${isCompleted ? "text-green-600" : "text-indigo-600"}`}
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h4
                        className={`font-semibold ${isCompleted ? "text-green-900 line-through" : "text-slate-900"}`}
                      >
                        {rec.title}
                      </h4>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={`${priorityColors[rec.priority]} border text-xs`}
                        >
                          {rec.priority}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleComplete(index)}
                          className={`p-1 ${isCompleted ? "text-green-600" : "text-slate-400"}`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <p
                      className={`text-sm mb-3 ${isCompleted ? "text-green-700" : "text-slate-700"}`}
                    >
                      {rec.description}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {rec.timeline}
                      </span>
                      <span className="capitalize">
                        {rec.category} • {rec.priority} priority
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-900">Progress Tracking</span>
          </div>
          <p className="text-sm text-blue-800">
            Complete {completedItems.size} of {recommendations.length}{" "}
            recommendations. Your health score will update as you implement
            these changes.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
